import { PaymentModuleId } from '@foundation/apis/payments';
import { SupportedLanguage } from '@foundation/localization';
import Clarity from '@microsoft/clarity';
import { useAuth } from '@shared/auth';
import { useTranslation } from '@shared/localization';
import cn from 'clsx';
import { useEffect, useRef, useState, JSX } from 'react';
import { useSearchParams } from 'react-router-dom';

import { disableUIBlocker, enableUIBlocker } from '@/components/common';
import { RedirectStatus } from '@/components/contexts/params';
import { PayButton } from '@/components/pages/stored-instruments/utils/PayButton';
import { useUserFriendlyPaymentModuleName } from '@/hooks/useUserFriendlyPaymentModuleName';
import { triggerErrorToast, triggerInfoToast } from '@/libs/toasts';
import { PaymentIntentFlow } from '@/schemas/PaymentIntent';
import BraintreeSepaService from '@/services/braintree/BraintreeSepaService';
import {
  BraintreeError,
  BraintreeErrorCodes,
} from '@/services/braintree/errors/BraintreeError';
import Logger, { LoggerOrigin } from '@/services/Logger';
import NavigationService, {
  InternalPage,
} from '@/services/navigation/NavigationService';
import PaymentStore from '@/services/payments/PaymentStore';
import TelemetryService from '@/services/telemetry/TelemetryService';
import { getQaTestingProps, QaTestingElements } from '@/utils/qaTesting';

interface IBraintreeSepaFormProps {
  navigation?: JSX.Element | null;
}

const styles = {
  input:
    'relative rounded-field bg-secondary h-14 p-sm focused-container w-full border-2 border-transparent ring-1 ring-silver-700 bg-secondary shadow-on-focus focus-visible:ring-1 focus-visible:ring-silver-700',
  buttonContainer: 'flex mt-md space-x-2',
  outerContainer: 'p-sm bg-white ring-1 ring-silver-700 rounded-md',
};

export function BraintreeSepaForm({ navigation }: IBraintreeSepaFormProps) {
  const { userId } = useAuth();
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const paymentModuleT = useUserFriendlyPaymentModuleName();

  const [searchParams, setSearchParams] = useSearchParams();

  const [accountHolderNameValue, setAccountHolderNameValue] = useState('');
  const [accountHolderNameValid, setAccountHolderNameValid] = useState(false);
  const [ibanValue, setIbanValue] = useState('');
  const [ibanValid, setIbanValid] = useState(false);

  const ibanFieldRef = useRef<HTMLInputElement>(null);
  const accountHolderNameFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ibanValue?.trim()?.length <= 0) {
      setIbanValid(false);
      ibanFieldRef.current?.classList?.remove('border-transparent');
      ibanFieldRef.current?.classList?.add('border-error');
    } else {
      setIbanValid(true);
      ibanFieldRef.current?.classList?.remove('border-error');
      ibanFieldRef.current?.classList?.add('border-transparent');
    }

    if (accountHolderNameValue?.trim()?.length <= 0) {
      setAccountHolderNameValid(false);
      accountHolderNameFieldRef.current?.classList?.remove(
        'border-transparent',
      );
      accountHolderNameFieldRef.current?.classList?.add('border-error');
    } else {
      setAccountHolderNameValid(true);
      accountHolderNameFieldRef.current?.classList?.remove('border-error');
      accountHolderNameFieldRef.current?.classList?.add('border-transparent');
    }
  }, [ibanValue, accountHolderNameValue]);

  const submitSepaForm = async (accountHolderName: string, iban: string) => {
    enableUIBlocker();

    BraintreeSepaService.submitSepaForm(
      accountHolderName,
      iban,
      language as SupportedLanguage,
      userId,
    )
      .then(() => {
        if (PaymentStore.flow === PaymentIntentFlow.Manage) {
          searchParams.set('status', RedirectStatus.Success);
          setSearchParams(searchParams);
          window.location.reload();
        } else {
          NavigationService.navigateTo({ page: InternalPage.StatusSuccess });
          disableUIBlocker();
        }
        Clarity.setTag('pay', [
          RedirectStatus.Success,
          paymentModuleT(PaymentModuleId.BraintreeSepa),
        ]);
      })
      .catch((error: Error) => {
        disableUIBlocker();
        TelemetryService.trackException(error, 'BraintreeSepaForm', {
          pid: PaymentStore.paymentIntentId,
        });
        Logger.error(
          LoggerOrigin.ReactComponent,
          'BraintreeSepaForm failed to add SEPA mandate.',
          error,
        );

        if (error !== undefined && error.name === 'BraintreeError') {
          const braintreeError = error as BraintreeError;

          if (
            braintreeError.code === BraintreeErrorCodes.SepaCreateMandateFailed
          ) {
            Clarity.setTag('pay', [
              RedirectStatus.Failure,
              paymentModuleT(PaymentModuleId.BraintreeSepa),
            ]);

            triggerErrorToast(
              t('Something went wrong!'),
              t(
                'Operation failed. Please try again with a different payment method.',
              ),
            );
          } else if (
            braintreeError.code === BraintreeErrorCodes.SepaCustomerCanceled
          ) {
            Clarity.setTag('pay', [
              RedirectStatus.Canceled,
              paymentModuleT(PaymentModuleId.BraintreeSepa),
            ]);
            triggerInfoToast(
              t('Select a new payment method and proceed to pay.'),
            );
          }
        } else {
          Clarity.setTag('pay', [
            RedirectStatus.Failure,
            paymentModuleT(PaymentModuleId.BraintreeSepa),
          ]);

          triggerErrorToast(
            t('Something went wrong!'),
            t(
              'Operation failed. Please try again with a different payment method.',
            ),
          );
        }
      });
  };

  return (
    <div className={cn(styles.outerContainer)}>
      <input
        id="accountHolderName"
        placeholder={t('Account Holder Name')}
        type="text"
        value={accountHolderNameValue}
        onChange={(e) => setAccountHolderNameValue(e.target.value)}
        className={cn(styles.input)}
        ref={accountHolderNameFieldRef}
        {...getQaTestingProps(
          QaTestingElements.AccountHolderName,
          accountHolderNameValue,
        )}
      />

      <input
        id="iban"
        placeholder="IBAN"
        type="text"
        value={ibanValue}
        onChange={(e) => setIbanValue(e.target.value)}
        className={cn('mt-2', styles.input)}
        ref={ibanFieldRef}
        {...getQaTestingProps(QaTestingElements.AccountIban, ibanValue)}
      />

      <div className={cn('flex mt-md space-x-2')}>
        {navigation}
        <PayButton
          onClick={() => submitSepaForm(accountHolderNameValue, ibanValue)}
          title={
            PaymentStore.flow === PaymentIntentFlow.Manage
              ? t('Add new SEPA mandate')
              : t('Proceed to pay')
          }
          disabled={!ibanValid || !accountHolderNameValid}
        />
      </div>
    </div>
  );
}
